import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { CardActions } from '@material-ui/core';
import WizardStepContainer from '../WizardStepContainer';
import { WizardStylesContext } from '../WizardStylesContext';
import { navigateToOption } from '../../../utils/marketIdPathFunctions';
import { useHistory } from 'react-router';
import { processTextAndFilesForSave } from '../../../api/files';
import { getQuillStoredState, resetEditor } from '../../TextEditors/Utilities/CoreUtils';
import _ from 'lodash';
import { OperationInProgressContext } from '../../../contexts/OperationInProgressContext/OperationInProgressContext';
import NameField, { clearNameStoredState, getNameStoredState } from '../../TextFields/NameField';
import SpinningIconLabelButton from '../../Buttons/SpinningIconLabelButton';
import { Clear, SettingsBackupRestore } from '@material-ui/icons';
import { useIntl } from 'react-intl';
import { HASH_MENTION_CHARS, useEditor } from '../../TextEditors/quillHooks';
import { realeaseInvestibleEditLock, updateInvestible } from '../../../api/investibles';
import { refreshInvestibles } from '../../../contexts/InvestibesContext/investiblesContextHelper';
import { InvestiblesContext } from '../../../contexts/InvestibesContext/InvestiblesContext';
import { DiffContext } from '../../../contexts/DiffContext/DiffContext';
import CommentBox from '../../../containers/CommentBox/CommentBox';

function OptionEditStep(props) {
  const { marketId, investible, updateFormData = () => {}, formData = {}, parentComment } = props;
  const intl = useIntl();
  const [, investiblesDispatch] = useContext(InvestiblesContext);
  const [, diffDispatch] = useContext(DiffContext);
  const [, setOperationRunning] = useContext(OperationInProgressContext);
  const history = useHistory();
  const classes = useContext(WizardStylesContext);
  const [hasValue, setHasValue] = useState(true);
  const investibleId = investible.id;
  const { description, version } = investible || {};
  const { uploadedFiles } = formData;
  const editorName = `body-editor${investibleId}`;
  const useDescription = getQuillStoredState(editorName) || description;
  const { useCompression } = formData;
  const editorSpec = {
    onUpload: (files) => updateFormData({uploadedFiles: files}),
    marketId,
    mentionsAllowed: true,
    mentionDenotationChars: HASH_MENTION_CHARS,
    placeholder: intl.formatMessage({ id: 'investibleAddDescriptionDefault' }),
    value: useDescription,
    // B-all-535: the name field above owns arrival focus; both autofocusing was a timer race
    autoFocus: false
  };

  const [Editor] = useEditor(editorName, editorSpec);

  function handleSave() {
    // uploaded files on edit is the union of the new uploaded files and the old uploaded files
    const oldInvestibleUploadedFiles = investible.uploaded_files || [];
    const currentUploadedFiles = uploadedFiles || [];
    const newUploadedFiles = _.uniqBy([...currentUploadedFiles, ...oldInvestibleUploadedFiles],
      'path');
    const description = getQuillStoredState(editorName) !== null ? getQuillStoredState(editorName) :
      useDescription;
    const name = getNameStoredState(investibleId);
    const {
      uploadedFiles: filteredUploads,
      text: tokensRemoved,
    } = processTextAndFilesForSave(newUploadedFiles, description);
    const updateInfo = {
      uploadedFiles: filteredUploads,
      name,
      description: tokensRemoved,
      marketId,
      investibleId,
      version
    };
    return updateInvestible(updateInfo)
      .then((fullInvestible) => {
        setOperationRunning(false);
        resetEditor(editorName);
        clearNameStoredState(investibleId);
        refreshInvestibles(investiblesDispatch, diffDispatch, [fullInvestible]);
        navigateToOption(history, parentComment.market_id, parentComment.investible_id,
          parentComment.group_id, investibleId);
      });
  }

  function onCancel() {
    resetEditor(editorName);
    return realeaseInvestibleEditLock(marketId, investibleId).then((newInv) => {
      setOperationRunning(false);
      refreshInvestibles(investiblesDispatch, diffDispatch, [newInv]);
      navigateToOption(history, parentComment.market_id, parentComment.investible_id,
        parentComment.group_id, investibleId);
    });
  }

  return (
    <WizardStepContainer
      {...props}
      isLarge
    >
      <CommentBox
        comments={[parentComment]}
        marketId={parentComment.market_id}
        allowedTypes={[]}
        removeActions={true}
        showVoting={false}
        isInbox
        compressAll
        inboxMessageId={parentComment?.id}
        toggleCompression={() => updateFormData({ useCompression: !useCompression })}
        useCompression={useCompression}
      />
      <div className={classes.borderBottom}/>
      <NameField id={investibleId} setHasValue={setHasValue} placeHolder="optionTitlePlaceholder"/>
      {Editor}
      <CardActions className={classes.actions}>
        <SpinningIconLabelButton
          icon={SettingsBackupRestore}
          disabled={!hasValue}
          onClick={handleSave}
          whiteBackground
          id="investibleUpdateButton"
        >
          {intl.formatMessage({ id: 'update'})}
        </SpinningIconLabelButton>
        <SpinningIconLabelButton onClick={onCancel} icon={Clear} id="marketAddCancelButton" whiteBackground>
          {intl.formatMessage({ id: 'marketAddCancelLabel' })}
        </SpinningIconLabelButton>
      </CardActions>
    </WizardStepContainer>
  )
}

OptionEditStep.propTypes = {
  updateFormData: PropTypes.func,
  formData: PropTypes.object,
}

export default OptionEditStep